import React, { useState, useEffect, useRef } from 'react';
import './styles.css';
import arrowRight from '../../images/arrowRight.svg';
import PlanSelector from '../planSelector/planSelector';
import LocationPanel from '../locationPanel/locationPanel';
import DistancePanel from '../distancePanel/distancePanel';
import { useLocation } from '../../contexts/LocationContext';

const SearchLocation = ({ 
    onPlanChange, 
    onDistanceChange, 
    showPlanError, 
    selectedPlan, 
    setSelectedPlan,
    locationAddress,
    onLocationChange,
    initialDistance = '25',
    envPlanCode = '',
    // When the parent (search.jsx) detects that browser/IP geolocation resolved OUTSIDE the
    // plan's state, or otherwise failed to produce a usable address, it bumps this counter
    // and supplies a message — this component opens the LocationPanel automatically (as if
    // the user had clicked "Edit location") WITH that message already showing, instead of
    // silently falling back to a central-zip location with no explanation. A counter (rather
    // than a boolean) so the SAME message re-triggers the panel again on a repeat failure.
    autoOpenLocationPanelSignal = 0,
    autoOpenLocationPanelError = ''
}) => {
    const { updateUserLocation } = useLocation();
    // Seeded from the parent's (search.jsx) already-restored `distance` value via a lazy
    // initializer, NOT always the hardcoded '25' default this used to start at unconditionally.
    // That was the actual cause of "distance lost going from Results back to Search": search.jsx
    // correctly seeds its OWN `distance` state from SearchStateContext continuity (Phase 3), but
    // never told THIS component about it — so this component's local `distance` always started
    // at '25' regardless, and the "notify parent of initial value" effect a few lines down then
    // immediately reported that hardcoded '25' back up to the parent, silently overwriting the
    // correct restored value a moment after mount. This component's OWN `distance` state is also
    // what actually drives the on-screen "Within {distance} miles" text below, so the bug was
    // visible to the user, not just an internal state mismatch.
    const [distance, setDistance] = useState(initialDistance);
    const [isLocationPanelOpen, setIsLocationPanelOpen] = useState(false);
    const [isDistancePanelOpen, setIsDistancePanelOpen] = useState(false);
    const [locationPanelStateError, setLocationPanelStateError] = useState('');

    // Auto-open the location panel (pre-populated with the supplied error) whenever the
    // parent bumps autoOpenLocationPanelSignal — see prop comment above. Skips the very
    // first render (signal starts at 0) via the ref so mounting never auto-opens the panel.
    const lastAutoOpenSignalRef = useRef(0);
    useEffect(() => {
        if (autoOpenLocationPanelSignal && autoOpenLocationPanelSignal !== lastAutoOpenSignalRef.current) {
            lastAutoOpenSignalRef.current = autoOpenLocationPanelSignal;
            setLocationPanelStateError(autoOpenLocationPanelError || '');
            setIsLocationPanelOpen(true);
        }
    }, [autoOpenLocationPanelSignal, autoOpenLocationPanelError]);
    
    // Use location address from parent, default to Florida if not provided
    const [userLocation, setUserLocation] = useState(locationAddress || '');

    // Update local state when parent's locationAddress changes
    useEffect(() => {
        if (locationAddress) {
            setUserLocation(locationAddress);
        }
    }, [locationAddress]);

    // Confirm the (now correctly-seeded, see the useState initializer above) initial distance
    // value back up to the parent on mount. This used to be what silently reset distance back
    // to '25' on every Search-page mount, since `distance` itself used to always start at that
    // hardcoded default — now that it's seeded from `initialDistance`, this is a harmless,
    // idempotent confirmation of a value the parent already has, not an overwrite.
    useEffect(() => {
        if (onDistanceChange) {
            onDistanceChange(distance);
        }
    }, []);

    const handleEditLocationClick = () => {
        // Clear any leftover auto-open error from a previous geolocation failure so a manual
        // "Edit location" click always opens to a clean panel, not a stale message.
        setLocationPanelStateError('');
        setIsLocationPanelOpen(true);
    };

    const handleLocationPanelClose = () => {
        setIsLocationPanelOpen(false);
    };

    const handleEditDistanceClick = () => {
        setIsDistancePanelOpen(true);
    };

    const handleDistancePanelClose = () => {
        setIsDistancePanelOpen(false);
    };

    const handleDistanceSelect = (newDistance) => {
        setDistance(newDistance);
        if (onDistanceChange) {
            onDistanceChange(newDistance);
        }
    };

    const handleLocationSelect = (locationData) => {
        console.log('📍 Location selected:', locationData);
        
        // locationData is now an object with { latitude, longitude, city, stateCode, zipCode, formattedAddress }
        // Extract the display string
        const displayAddress = typeof locationData === 'string' 
            ? locationData 
            : (locationData.formattedAddress || `${locationData.city}, ${locationData.stateCode} ${locationData.zipCode}`);
        
        setUserLocation(displayAddress);

        // Persist full location (including lat/lon) to context so handleSearch can read coords.
        // Tagged with envPlanCode (the current state's env code, passed down from search.jsx)
        // so a later page load into a DIFFERENT state can recognize this as belonging to the
        // WRONG state and refuse to reuse it — see search.jsx's currentStateLocation and
        // results.jsx's getLocationWithFallback for where that tag gets validated.
        if (typeof locationData === 'object' && locationData.latitude && locationData.longitude) {
            updateUserLocation({ ...locationData, planCode: envPlanCode, timestamp: Date.now() });
        }

        // Notify parent of location change (pass display string)
        if (onLocationChange) {
            onLocationChange(displayAddress);
        }
    };
    
    return (
        <>
            <div id="searchLocationDiv">
                <h2 className="mb30">
                    What are you looking for today?
                </h2>
                <p className="mb30 userLocationText">
                    {/* A11y fix: this previously had aria-label="Edit current location",
                        which didn't match the visible text "Edit location" — accessible
                        name must contain the visible label in the same order with no
                        extra words inserted (WCAG 2.5.3 Label in Name). The visible text
                        alone is already a clear, unambiguous accessible name, so the
                        mismatched aria-label is simply removed rather than reworded. */}
                    <span className="search-near-label">Search near:</span> {userLocation} <button type="button" className="edit-location-link" onClick={handleEditLocationClick}>Edit location <img src={arrowRight} className="arrowRight" alt="" aria-hidden="true"/></button>
                </p>

                <p className="mb30 userLocationText distance-text-line">
                    <span className="search-near-label">Distance:</span> Within {distance} miles <button type="button" className="edit-location-link" onClick={handleEditDistanceClick} aria-label="Edit distance">Edit distance <img src={arrowRight} className="arrowRight" alt="" aria-hidden="true"/></button>
                </p>
                
                <PlanSelector 
                    onPlanChange={onPlanChange}
                    showPlanError={showPlanError}
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                />
            </div>

            <LocationPanel
                isOpen={isLocationPanelOpen}
                onClose={handleLocationPanelClose}
                onLocationSelect={handleLocationSelect}
                initialStateError={locationPanelStateError}
            />

            <DistancePanel
                isOpen={isDistancePanelOpen}
                onClose={handleDistancePanelClose}
                currentDistance={distance}
                onDistanceSelect={handleDistanceSelect}
            />
        </>
    );
};

export default SearchLocation;